import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import './datatables-dark.css';

DataTable.use(DT);

// Backend API base URL
const API_BASE = process.env.REACT_APP_API_BASE || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const initialDevices = [
    { type: 'PS5', name: 'PS5 #1', img: 'https://img.icons8.com/?size=100&id=rseRGUUHZGNU&format=png&color=ffffff', rate: 18000 },
    { type: 'PS5', name: 'PS5 #2', img: 'https://img.icons8.com/?size=100&id=rseRGUUHZGNU&format=png&color=ffffff', rate: 18000 },
    { type: 'PS4', name: 'PS4 #1', img: 'https://img.icons8.com/?size=100&id=zBcnw2-gQOt2&format=png&color=ffffff', rate: 12000 },
    { type: 'PS4', name: 'PS4 #2', img: 'https://img.icons8.com/?size=100&id=zBcnw2-gQOt2&format=png&color=ffffff', rate: 12000 },
    { type: 'PS4', name: 'PS4 #3', img: 'https://img.icons8.com/?size=100&id=zBcnw2-gQOt2&format=png&color=ffffff', rate: 12000 },
    { type: 'PS4', name: 'PS4 #4', img: 'https://img.icons8.com/?size=100&id=zBcnw2-gQOt2&format=png&color=ffffff', rate: 12000 },
    { type: 'PC', name: 'PC #1', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'PC', name: 'PC #2', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'PC', name: 'PC #3', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'PC', name: 'PC #4', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'PC', name: 'PC #5', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'PC', name: 'PC #6', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
    { type: 'Bilardo', name: 'Bilardo Table', img: 'https://img.icons8.com/?size=100&id=PSYB5B3EHx6s&format=png&color=000000', rate: 30000 }
];


function App() {
    const [showSession, setShowSession] = useState(() => {
        const saved = localStorage.getItem('showSession');
        return saved !== null ? saved === 'true' : true;
    });
    // Load persisted state
    const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem('loggedIn') === 'true');
    const [staffName, setStaffName] = useState(() => localStorage.getItem('staffName') || "");
    const [activeSessions, setActiveSessions] = useState(() => {
        const saved = localStorage.getItem('activeSessions');
        return saved ? JSON.parse(saved) : [];
    });
    const [snack, setSnack] = useState("");
    const [snackQty, setSnackQty] = useState(1);
    const [snackPrice, setSnackPrice] = useState(10);

    // Local snack state for each session
    const [sessionSnacks, setSessionSnacks] = useState({});

    // Direct snack sale states
    const [snackError, setSnackError] = useState("");
    const [snackSuccess, setSnackSuccess] = useState("");
    const [rate, setRate] = useState(() => Number(localStorage.getItem('rate')) || 30);
    const [devices, setDevices] = useState(() => {
        const saved = localStorage.getItem('devices');
        if (!saved) return initialDevices;
        try {
            const parsed = JSON.parse(saved);
            // Merge any new defaults that aren't already cached (matched by name),
            // so devices added to initialDevices in a later release show up automatically
            // without forcing existing users to clear localStorage. User edits to
            // existing devices (renames, rate changes) are preserved.
            const existingNames = new Set(parsed.map(d => d.name));
            const missing = initialDevices.filter(d => !existingNames.has(d.name));
            return missing.length > 0 ? [...parsed, ...missing] : parsed;
        } catch {
            return initialDevices;
        }
    });
    const [endingSessionIds, setEndingSessionIds] = useState([]);
    const [startingDeviceNames, setStartingDeviceNames] = useState([]);
    const [saleSummaries, setSaleSummaries] = useState([]);
    const [showReports, setShowReports] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [totalRecords, setTotalRecords] = useState(0);
    // Sum of price across the full filtered set (computed server-side, not just the loaded page).
    const [filteredTotalPrice, setFilteredTotalPrice] = useState(0);
    // Imperative handle to the Reports sales DataTable, so filter changes can
    // trigger a server reload via ajax.reload() without remounting the table.
    const salesTableRef = useRef(null);

    // Filter states for reports
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterStartTime, setFilterStartTime] = useState('');
    const [filterEndTime, setFilterEndTime] = useState('');
    // Latest filter values for the table's ajax callback to read (avoids stale closures).
    const salesFiltersRef = useRef({});

    // Income analytics state
    const [incomeAnalytics, setIncomeAnalytics] = useState({
        day: { totalIncome: 0, count: 0 },
        month: { totalIncome: 0, count: 0 },
        year: { totalIncome: 0, count: 0 },
        devices: {
            PC: { totalIncome: 0, count: 0 },
            PS4: { totalIncome: 0, count: 0 },
            PS5: { totalIncome: 0, count: 0 },
            Bilardo: { totalIncome: 0, count: 0 },
            Snack: { totalIncome: 0, count: 0 }
        }
    });

    // Auth state
    const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken') || "");
    const [role, setRole] = useState(() => localStorage.getItem('role') || "");

    // Sync loggedIn with authToken on mount, AND always keep the axios
    // Authorization header in sync with the current token. The header lives in
    // module state (not localStorage), so on a hard refresh both authToken and
    // loggedIn restore from localStorage but the header is empty until we set it.
    useEffect(() => {
        if (authToken) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
            if (!loggedIn) {
                setLoggedIn(true);
                localStorage.setItem('loggedIn', 'true');
            }
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [authToken]);
    const [loginUsername, setLoginUsername] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [loginError, setLoginError] = useState("");

    // Global auth guard: protected write routes reject an expired/invalid JWT with
    // 401/403 (tokens last 1 day). Without this, every add/remove/edit would fail
    // silently. Catch it once, clear the stale session, and send the user back to
    // the login screen with a clear message instead of a quiet no-op.
    useEffect(() => {
        const interceptorId = axios.interceptors.response.use(
            res => res,
            err => {
                const status = err.response?.status;
                const url = err.config?.url || '';
                // The login request's own 401/403 (wrong password) is handled inline there.
                if ((status === 401 || status === 403) && !url.includes('/login')) {
                    setLoggedIn(false);
                    setAuthToken('');
                    setRole('');
                    setStaffName('');
                    localStorage.removeItem('loggedIn');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('role');
                    localStorage.removeItem('staffName');
                    delete axios.defaults.headers.common['Authorization'];
                    setLoginError('Your session has expired. Please log in again.');
                }
                return Promise.reject(err);
            }
        );
        return () => axios.interceptors.response.eject(interceptorId);
    }, []);

    // Registration state
    const [showRegister, setShowRegister] = useState(false);
    const [regUsername, setRegUsername] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regRole, setRegRole] = useState("staff");
    const [regError, setRegError] = useState("");
    const [regSuccess, setRegSuccess] = useState("");

    // Store management state
    const [showStore, setShowStore] = useState(false);
    const [inventory, setInventory] = useState([]);
    const [newItemName, setNewItemName] = useState("");
    const [newItemStock, setNewItemStock] = useState(0);
    const [newItemPrice, setNewItemPrice] = useState(0);
    const [storeError, setStoreError] = useState("");
    const [storeSuccess, setStoreSuccess] = useState("");
    // In-progress stock edits keyed by inventory _id while the user is typing in the
    // Stock cell. Only committed (with delta = typed - original) on blur / Enter.
    const [stockInputs, setStockInputs] = useState({});
    // Inventory filter / sort UI state
    const [inventorySearch, setInventorySearch] = useState("");
    const [inventoryStockFilter, setInventoryStockFilter] = useState("all"); // all | in | low | out
    const [inventorySortBy, setInventorySortBy] = useState("item");          // item | stock | price | created
    const [inventorySortDir, setInventorySortDir] = useState("asc");          // asc | desc
    const [inventoryStartDate, setInventoryStartDate] = useState("");         // YYYY-MM-DD, filters by item creation
    const [inventoryEndDate, setInventoryEndDate] = useState("");
    // Stock movements log (audit of every +/- to inventory stock)
    const [stockMovementsTotals, setStockMovementsTotals] = useState({ totalIncome: 0, totalOutput: 0, net: 0, count: 0 });
    // Server-side Stock Movements DataTable handle + latest filters for its ajax loader.
    const movementsTableRef = useRef(null);
    const movementsFiltersRef = useRef({});
    const [movementsStartDate, setMovementsStartDate] = useState("");
    const [movementsEndDate, setMovementsEndDate] = useState("");
    const [movementsItemFilter, setMovementsItemFilter] = useState("");        // empty = all items
    const [movementsTypeFilter, setMovementsTypeFilter] = useState("all");      // all | income | output
    const [movementsRefreshTrigger, setMovementsRefreshTrigger] = useState(0);  // bump after mutations to refetch

    // Payments state
    const [showPayments, setShowPayments] = useState(false);
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [paymentsFilterStartDate, setPaymentsFilterStartDate] = useState('');
    const [paymentsFilterEndDate, setPaymentsFilterEndDate] = useState('');
    const [paymentsFilterStartTime, setPaymentsFilterStartTime] = useState('');
    const [paymentsFilterEndTime, setPaymentsFilterEndTime] = useState('');
    const [paymentsTotal, setPaymentsTotal] = useState(0);
    // Sum of amount across the full filtered set (computed server-side).
    const [paymentsFilteredTotal, setPaymentsFilteredTotal] = useState(0);
    // Server-side Payments DataTable handle + latest filters for its ajax loader.
    const paymentsTableRef = useRef(null);
    const paymentsFiltersRef = useRef({});
    // Bumped after recording a payment so the table + analytics refetch.
    const [paymentsRefreshTrigger, setPaymentsRefreshTrigger] = useState(0);
    const [paymentAnalytics, setPaymentAnalytics] = useState({
        total: { total: 0, totalAmount: 0 },
        month: { total: 0, totalAmount: 0 },
        year: { total: 0, totalAmount: 0 }
    });
    const [netProfitData, setNetProfitData] = useState(null);
    const [netProfitMonthData, setNetProfitMonthData] = useState(null);
    const [netProfitRangeData, setNetProfitRangeData] = useState(null);
    const [newPaymentAmount, setNewPaymentAmount] = useState(0);
    const [newPaymentReference, setNewPaymentReference] = useState("");
    const [newPaymentNote, setNewPaymentNote] = useState("");
    const [newPaymentFile, setNewPaymentFile] = useState(null);
    const [paymentError, setPaymentError] = useState("");
    const [paymentSuccess, setPaymentSuccess] = useState("");

    // Promise-based confirmation dialog, shared by every add/remove/edit action
    // across all pages. askConfirm(message) shows the themed modal and resolves
    // true (Confirm) or false (Cancel), so callers can `if (!(await askConfirm(...))) return;`.
    const [confirmDialog, setConfirmDialog] = useState(null); // { message, confirmLabel, danger } | null
    const confirmResolveRef = useRef(null);
    const askConfirm = (message, { confirmLabel = 'Confirm', danger = false } = {}) =>
        new Promise(resolve => {
            confirmResolveRef.current = resolve;
            setConfirmDialog({ message, confirmLabel, danger });
        });
    const resolveConfirm = (result) => {
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        setConfirmDialog(null);
        if (resolve) resolve(result);
    };
    // Original price captured on focus of an inline price input, so the price-edit
    // confirmation only fires when the value actually changed (blur alone shouldn't nag).
    const priceEditOriginalRef = useRef({});
    // Same idea for the per-device hourly rate inputs on the Sessions page.
    const deviceRateOriginalRef = useRef({});

    // Commit a device rate edit: confirm when the value changed on blur, and revert
    // to the pre-edit value if the user declines. Rate lives only in localStorage.
    const commitDeviceRate = async (idx) => {
        const original = deviceRateOriginalRef.current[idx];
        delete deviceRateOriginalRef.current[idx];
        const current = devices[idx]?.rate;
        if (original === undefined || Number(original) === Number(current)) return;
        const ok = await askConfirm(`Change the hourly rate for "${devices[idx].name}" from ${original} to ${current}?`, { confirmLabel: 'Update Rate' });
        if (!ok) {
            const reverted = devices.map((d, i) => i === idx ? { ...d, rate: Number(original) } : d);
            setDevices(reverted);
            localStorage.setItem('devices', JSON.stringify(reverted));
        }
    };

    // Build the server-side filter params (date range + category) from current
    // filter state. Shared by the table's ajax loader and the Excel export so
    // both honour the same window. Returns a plain object for axios `params`.
    const buildSalesFilterParams = () => {
        const params = {};
        if (filterCategory) params.category = filterCategory;
        if (filterStartDate) {
            params.startDate = filterStartTime ? `${filterStartDate}T${filterStartTime}` : `${filterStartDate}T00:00`;
        }
        if (filterEndDate) {
            params.endDate = filterEndTime ? `${filterEndDate}T${filterEndTime}` : `${filterEndDate}T23:59:59`;
        }
        return params;
    };

    // Turn one server row (raw timestamps + joined session times) into the array
    // the DataTable renders. Date/time are formatted here so they use the
    // browser's locale and timezone, exactly as before.
    const formatSalesRow = (r) => {
        const saleDate = new Date(r.timestamp);
        let timeDisplay;
        if (r.category === 'Snack') {
            timeDisplay = saleDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (r.sessionStart && r.sessionEnd) {
            const s = new Date(r.sessionStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const e = new Date(r.sessionEnd).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            timeDisplay = `${s} - ${e}`;
        } else {
            timeDisplay = 'N/A';
        }
        return [
            saleDate.toLocaleDateString(),
            timeDisplay,
            r.category,
            r.item,
            r.amount,
            Math.round(r.price).toLocaleString(),
            r.staff || '-'
        ];
    };

    // DataTables server-side ajax loader. DataTables calls this with its request
    // (paging/search/order) and a callback; we merge the report filters and hit
    // the /sales/datatable endpoint, returning only the current page's rows.
    const salesAjax = (dtData, callback) => {
        const params = {
            draw: dtData.draw,
            start: dtData.start,
            length: dtData.length,
            search: (dtData.search && dtData.search.value) || '',
            orderColumn: (dtData.order && dtData.order[0] && dtData.order[0].column) ?? 0,
            orderDir: (dtData.order && dtData.order[0] && dtData.order[0].dir) || 'desc',
            ...salesFiltersRef.current
        };
        axios.get(`${API_BASE}/sales/datatable`, { params }).then(res => {
            const d = res.data;
            setTotalRecords(d.recordsTotal || 0);
            setFilteredTotalPrice(d.filteredTotal || 0);
            callback({
                draw: d.draw,
                recordsTotal: d.recordsTotal,
                recordsFiltered: d.recordsFiltered,
                data: (d.data || []).map(formatSalesRow)
            });
        }).catch(err => {
            console.error('Error loading sales:', err);
            callback({ draw: dtData.draw, recordsTotal: 0, recordsFiltered: 0, data: [] });
        });
    };

    // Income analytics — independent of the table filters, fetched once the
    // Reports view opens.
    useEffect(() => {
        if (!showReports) return;
        const fetchIncomeAnalytics = async () => {
            try {
                const [dayRes, monthRes, yearRes, pcRes, ps4Res, ps5Res, bilardoRes, snackRes] = await Promise.all([
                    axios.get(`${API_BASE}/income/day`),
                    axios.get(`${API_BASE}/income/month`),
                    axios.get(`${API_BASE}/income/year`),
                    axios.get(`${API_BASE}/income/device/PC`),
                    axios.get(`${API_BASE}/income/device/PS4`),
                    axios.get(`${API_BASE}/income/device/PS5`),
                    axios.get(`${API_BASE}/income/device/Bilardo`),
                    axios.get(`${API_BASE}/income/device/Snack`)
                ]);

                setIncomeAnalytics({
                    day: dayRes.data,
                    month: monthRes.data,
                    year: yearRes.data,
                    devices: {
                        PC: pcRes.data,
                        PS4: ps4Res.data,
                        PS5: ps5Res.data,
                        Bilardo: bilardoRes.data,
                        Snack: snackRes.data
                    }
                });
            } catch (err) {
                console.error('Error fetching income analytics:', err);
            }
        };
        fetchIncomeAnalytics();
    }, [showReports]);

    // Push the latest filters into the ref and ask the table to reload from the
    // server when a filter changes. The very first run only seeds the ref — the
    // table's own mount triggers the initial load, so we skip reloading there to
    // avoid a duplicate fetch.
    const salesFiltersSeeded = useRef(false);
    useEffect(() => {
        salesFiltersRef.current = buildSalesFilterParams();
        if (!salesFiltersSeeded.current) {
            salesFiltersSeeded.current = true;
            return;
        }
        const api = salesTableRef.current && salesTableRef.current.dt();
        if (api) api.ajax.reload();
    }, [filterCategory, filterStartDate, filterEndDate, filterStartTime, filterEndTime]);

    // Build the server-side filter params for the Stock Movements table. Shared by
    // its ajax loader so the table, totals cards and export honour the same filters.
    const buildMovementsFilterParams = () => {
        const params = {};
        if (movementsStartDate) params.startDate = `${movementsStartDate}T00:00`;
        if (movementsEndDate) params.endDate = `${movementsEndDate}T23:59:59`;
        if (movementsItemFilter) params.itemId = movementsItemFilter;
        if (movementsTypeFilter !== 'all') params.type = movementsTypeFilter;
        return params;
    };

    // DataTables server-side loader for the Stock Movements log. Returns the page's
    // rows plus the income/output/net/count totals for the summary cards.
    const movementsAjax = (dtData, callback) => {
        const params = {
            draw: dtData.draw,
            start: dtData.start,
            length: dtData.length,
            search: (dtData.search && dtData.search.value) || '',
            orderColumn: (dtData.order && dtData.order[0] && dtData.order[0].column) ?? 0,
            orderDir: (dtData.order && dtData.order[0] && dtData.order[0].dir) || 'desc',
            ...movementsFiltersRef.current
        };
        axios.get(`${API_BASE}/stock-movements/datatable`, { params }).then(res => {
            const d = res.data;
            setStockMovementsTotals({
                totalIncome: d.totalIncome || 0,
                totalOutput: d.totalOutput || 0,
                net: d.net || 0,
                count: d.count || 0
            });
            callback({
                draw: d.draw,
                recordsTotal: d.recordsTotal,
                recordsFiltered: d.recordsFiltered,
                data: (d.data || []).map(formatMovementRow)
            });
        }).catch(err => {
            console.error('Error fetching stock movements:', err);
            setStockMovementsTotals({ totalIncome: 0, totalOutput: 0, net: 0, count: 0 });
            callback({ draw: dtData.draw, recordsTotal: 0, recordsFiltered: 0, data: [] });
        });
    };

    // Seed the movements filter ref and reload the table when filters change or an
    // inventory mutation bumps movementsRefreshTrigger. The table's own mount drives
    // the initial load, so the first run only seeds the ref.
    const movementsFiltersSeeded = useRef(false);
    useEffect(() => {
        movementsFiltersRef.current = buildMovementsFilterParams();
        if (!movementsFiltersSeeded.current) {
            movementsFiltersSeeded.current = true;
            return;
        }
        const api = movementsTableRef.current && movementsTableRef.current.dt();
        if (api) api.ajax.reload();
    }, [movementsStartDate, movementsEndDate, movementsItemFilter, movementsTypeFilter, movementsRefreshTrigger]);

    // Fetch inventory for store management and snacks dropdown
    useEffect(() => {
        if (authToken) {
            axios.get(`${API_BASE}/inventory`).then(res => {
                setInventory(res.data);
                // Set default snack if not already set
                if (!snack && res.data.length > 0) {
                    setSnack(res.data[0].item);
                    setSnackPrice(res.data[0].price);
                }
                // Initialize session snacks for existing sessions (keyed by session _id)
                setSessionSnacks(prev => {
                    const updated = { ...prev };
                    activeSessions.forEach(sess => {
                        if (sess._id && !updated[sess._id]) {
                            updated[sess._id] = { snack: res.data[0]?.item || "", qty: 1, price: res.data[0]?.price || 10 };
                        }
                    });
                    return updated;
                });
            });
        }
    }, [authToken]);

    // Payment analytics + net profit for the Payments view. The payment rows
    // themselves load via the server-side DataTable (paymentsAjax), not here.
    useEffect(() => {
        if (showPayments && authToken) {
            // Fetch payment analytics
            const fetchPaymentAnalytics = async () => {
                try {
                    const [totalRes, monthRes, yearRes] = await Promise.all([
                        axios.get(`${API_BASE}/payments/total`),
                        axios.get(`${API_BASE}/payments/month`),
                        axios.get(`${API_BASE}/payments/year`)
                    ]);

                    setPaymentAnalytics({
                        total: totalRes.data,
                        month: monthRes.data,
                        year: yearRes.data
                    });
                } catch (err) {
                    console.error('Error fetching payment analytics:', err);
                }
            };

            fetchPaymentAnalytics();

            // Fetch net profit data — all-time, this month, and (when filters are set) the user's range
            const fetchNetProfit = async () => {
                try {
                    const now = new Date();
                    const monthStartISO = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();

                    const requests = [
                        axios.get(`${API_BASE}/netprofit`),
                        axios.get(`${API_BASE}/netprofit?startDate=${encodeURIComponent(monthStartISO)}`)
                    ];
                    const hasRangeFilter = paymentsFilterStartDate || paymentsFilterEndDate;
                    if (hasRangeFilter) {
                        const params = new URLSearchParams();
                        if (paymentsFilterStartDate) {
                            const startDateTime = paymentsFilterStartTime ? `${paymentsFilterStartDate}T${paymentsFilterStartTime}` : `${paymentsFilterStartDate}T00:00`;
                            params.append('startDate', startDateTime);
                        }
                        if (paymentsFilterEndDate) {
                            const endDateTime = paymentsFilterEndTime ? `${paymentsFilterEndDate}T${paymentsFilterEndTime}` : `${paymentsFilterEndDate}T23:59`;
                            params.append('endDate', endDateTime);
                        }
                        requests.push(axios.get(`${API_BASE}/netprofit?${params}`));
                    }

                    const responses = await Promise.all(requests);
                    setNetProfitData(responses[0].data);
                    setNetProfitMonthData(responses[1].data);
                    setNetProfitRangeData(hasRangeFilter ? responses[2].data : null);
                } catch (err) {
                    console.error('Error fetching net profit:', err);
                }
            };
            fetchNetProfit();
        }
    }, [showPayments, authToken, paymentsFilterStartDate, paymentsFilterEndDate, paymentsFilterStartTime, paymentsFilterEndTime, paymentsRefreshTrigger]);

    // Build the server-side filter params (date range) for the Payments table.
    const buildPaymentsFilterParams = () => {
        const params = {};
        if (paymentsFilterStartDate) {
            params.startDate = paymentsFilterStartTime ? `${paymentsFilterStartDate}T${paymentsFilterStartTime}` : `${paymentsFilterStartDate}T00:00`;
        }
        if (paymentsFilterEndDate) {
            params.endDate = paymentsFilterEndTime ? `${paymentsFilterEndDate}T${paymentsFilterEndTime}` : `${paymentsFilterEndDate}T23:59`;
        }
        return params;
    };

    // Turn one server payment row into the DataTable row array. Date/time formatted
    // in the browser locale; the invoice cell is an HTML link when an attachment exists.
    const formatPaymentRow = (p) => {
        const hasAttachment = p.attachment && p.attachment.contentType;
        const filename = hasAttachment ? (p.attachment.filename || 'invoice') : '';
        const escapedName = filename.replace(/"/g, '&quot;');
        const invoiceCell = hasAttachment
            ? `<a href="${API_BASE}/payments/${p._id}/attachment" target="_blank" rel="noopener" download="${escapedName}" style="color:#00e676;text-decoration:underline">View / Download</a>`
            : '-';
        return [
            new Date(p.timestamp).toLocaleDateString(),
            new Date(p.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            Math.round(p.amount).toLocaleString(),
            p.reference,
            p.note || '-',
            p.staff || '-',
            invoiceCell
        ];
    };

    // DataTables server-side loader for the Payments table.
    const paymentsAjax = (dtData, callback) => {
        const params = {
            draw: dtData.draw,
            start: dtData.start,
            length: dtData.length,
            search: (dtData.search && dtData.search.value) || '',
            orderColumn: (dtData.order && dtData.order[0] && dtData.order[0].column) ?? 0,
            orderDir: (dtData.order && dtData.order[0] && dtData.order[0].dir) || 'desc',
            ...paymentsFiltersRef.current
        };
        axios.get(`${API_BASE}/payments/datatable`, { params }).then(res => {
            const d = res.data;
            setPaymentsTotal(d.recordsTotal || 0);
            setPaymentsFilteredTotal(d.filteredTotal || 0);
            callback({
                draw: d.draw,
                recordsTotal: d.recordsTotal,
                recordsFiltered: d.recordsFiltered,
                data: (d.data || []).map(formatPaymentRow)
            });
        }).catch(err => {
            console.error('Error loading payments:', err);
            callback({ draw: dtData.draw, recordsTotal: 0, recordsFiltered: 0, data: [] });
        });
    };

    // Seed the payments filter ref and reload the table on filter change or after a
    // new payment is recorded. First run only seeds (the table mount drives the
    // initial load).
    const paymentsFiltersSeeded = useRef(false);
    useEffect(() => {
        paymentsFiltersRef.current = buildPaymentsFilterParams();
        if (!paymentsFiltersSeeded.current) {
            paymentsFiltersSeeded.current = true;
            return;
        }
        const api = paymentsTableRef.current && paymentsTableRef.current.dt();
        if (api) api.ajax.reload();
    }, [paymentsFilterStartDate, paymentsFilterEndDate, paymentsFilterStartTime, paymentsFilterEndTime, paymentsRefreshTrigger]);

    // Keep latest devices/rate accessible inside the periodic fetch without
    // restarting the interval on every edit and without staling the closure.
    const devicesRef = useRef(devices);
    const rateRef = useRef(rate);
    useEffect(() => { devicesRef.current = devices; }, [devices]);
    useEffect(() => { rateRef.current = rate; }, [rate]);

    // Fetch active sessions on load and periodically every 5 minutes
    useEffect(() => {
        if (!authToken) return;
        const fetchSessions = () => {
            axios.get(`${API_BASE}/sessions?active=true`).then(res => {
                setActiveSessions(prev => {
                    const active = res.data.map(sess => {
                        const existing = prev.find(a => a._id === sess._id);
                        const matchedDevice = devicesRef.current.find(d => d.name === sess.deviceType);
                        return {
                            _id: sess._id,
                            device: matchedDevice || { name: sess.deviceType, type: 'Unknown', img: 'https://img.icons8.com/?size=100&id=buti0ig4reqf&format=png&color=ffffff', rate: 10000 },
                            start: new Date(sess.startTime).getTime(),
                            rate: matchedDevice?.rate || rateRef.current,
                            openedBy: sess.openedBy || existing?.openedBy || '',
                            snacks: existing ? existing.snacks : []
                        };
                    });
                    return active;
                });
            }).catch(err => console.error('Failed to fetch active sessions:', err));
        };

        fetchSessions();
        const interval = setInterval(fetchSessions, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [authToken]);

    // Mirror activeSessions to localStorage so refreshes restore state.
    useEffect(() => {
        localStorage.setItem('activeSessions', JSON.stringify(activeSessions));
    }, [activeSessions]);

    // Auth login handler
    const handleAuthLogin = async () => {
        setLoginError("");
        if (!loginUsername.trim() || !loginPassword.trim()) {
            setLoginError("Username and password are required");
            return;
        }
        try {
            const res = await axios.post(`${API_BASE}/login`, {
                username: loginUsername.trim(),
                password: loginPassword
            });
            setAuthToken(res.data.token);
            setRole(res.data.role || 'staff');
            localStorage.setItem('authToken', res.data.token);
            localStorage.setItem('role', res.data.role || 'staff');
            axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
            setLoggedIn(true);
            setStaffName(loginUsername.trim());
            setLoginUsername("");
            setLoginPassword("");
        } catch (err) {
            setLoginError(err.response?.data?.message || err.response?.data?.error || "Login failed");
        }
    };

    // Signout handler
    const handleSignout = () => {
        setLoggedIn(false);
        setStaffName("");
        setAuthToken("");
        setRole("");
        localStorage.removeItem('loggedIn');
        localStorage.removeItem('staffName');
        localStorage.removeItem('authToken');
        localStorage.removeItem('role');
        delete axios.defaults.headers.common['Authorization'];
        // Optionally clear sessions if you want:
        // setActiveSessions([]);
        // localStorage.removeItem('activeSessions');
    };

    // Registration handler
    const handleRegister = async () => {
        setRegError("");
        setRegSuccess("");
        if (!regUsername.trim() || !regPassword.trim()) {
            setRegError("Username and password are required");
            return;
        }
        if (regPassword.length < 6) {
            setRegError("Password must be at least 6 characters long");
            return;
        }
        if (!(await askConfirm(`Create a new ${regRole} account "${regUsername.trim()}"?`, { confirmLabel: 'Register' }))) return;
        try {
            await axios.post(`${API_BASE}/register`, {
                username: regUsername.trim(),
                password: regPassword,
                role: regRole
            });
            setRegSuccess("Registration successful! You can now log in.");
            setShowRegister(false);
            setLoginUsername(regUsername.trim());
            setLoginPassword("");
            setRegUsername("");
            setRegPassword("");
        } catch (err) {
            setRegError(err.response?.data?.error || err.response?.data?.message || "Registration failed");
        }
    };

    // Add item
    const handleAddItem = async () => {
        setStoreError(""); setStoreSuccess("");
        if (!newItemName.trim()) {
            setStoreError("Item name required");
            return;
        }
        if (inventory.some(inv => inv.item === newItemName.trim())) {
            setStoreError("Item already exists");
            return;
        }
        const stockNum = Number(newItemStock);
        const priceNum = Number(newItemPrice);
        if (isNaN(stockNum) || stockNum < 0) {
            setStoreError("Stock must be a valid non-negative number");
            return;
        }
        if (isNaN(priceNum) || priceNum < 0) {
            setStoreError("Price must be a valid non-negative number");
            return;
        }
        if (!(await askConfirm(`Add "${newItemName.trim()}" to inventory (stock ${stockNum}, price ${priceNum})?`, { confirmLabel: 'Add Item' }))) return;
        try {
            await axios.post(`${API_BASE}/inventory/add`, { item: newItemName.trim(), stock: stockNum, price: priceNum, staff: staffName });
            setStoreSuccess("Item added");
            setNewItemName(""); setNewItemStock(0); setNewItemPrice(0);
            const res = await axios.get(`${API_BASE}/inventory`);
            setInventory(res.data);
            setMovementsRefreshTrigger(t => t + 1);
        } catch (err) {
            setStoreError(err.response?.data?.error || "Add failed");
        }
    };
    // Remove item
    const handleRemoveItem = async (id) => {
        setStoreError(""); setStoreSuccess("");
        const target = inventory.find(i => i._id === id);
        if (!(await askConfirm(`Remove "${target?.item || 'this item'}" from inventory? This cannot be undone.`, { confirmLabel: 'Remove', danger: true }))) return;
        try {
            await axios.delete(`${API_BASE}/inventory/${id}`, { data: { staff: staffName } });
            setStoreSuccess("Item removed");
            const res = await axios.get(`${API_BASE}/inventory`);
            setInventory(res.data);
            setMovementsRefreshTrigger(t => t + 1);
        } catch (err) {
            setStoreError(err.response?.data?.error || "Remove failed");
        }
    };
    // Commit an in-progress stock edit from the inline Stock input.
    // Computes delta = typed value − current stock and forwards to handleUpdateStock,
    // so the existing movements log entry still records a signed delta.
    const commitStockEdit = async (inv) => {
        const raw = stockInputs[inv._id];
        // Nothing typed → nothing to commit
        if (raw === undefined) return;
        const num = parseInt(raw, 10);
        // Invalid or negative → silently discard the edit and snap back
        if (Number.isNaN(num) || num < 0) {
            setStockInputs(prev => {
                const next = { ...prev };
                delete next[inv._id];
                return next;
            });
            return;
        }
        const delta = num - (inv.stock || 0);
        if (delta !== 0) {
            const verb = delta > 0 ? `add ${delta}` : `remove ${Math.abs(delta)}`;
            const ok = await askConfirm(`Update stock for "${inv.item}" from ${inv.stock || 0} to ${num} (${verb})?`, { confirmLabel: 'Update Stock' });
            if (ok) {
                await handleUpdateStock(inv._id, delta);
            }
        }
        setStockInputs(prev => {
            const next = { ...prev };
            delete next[inv._id];
            return next;
        });
    };

    // Update stock
    const handleUpdateStock = async (id, delta) => {
        setStoreError(""); setStoreSuccess("");
        const inv = inventory.find(i => i._id === id);
        if (!inv) {
            setStoreError("Item not found");
            return;
        }
        if (inv.stock + delta < 0) {
            setStoreError("Stock cannot be negative");
            return;
        }
        try {
            await axios.patch(`${API_BASE}/inventory/${id}`, {
                stock: delta,
                note: delta > 0 ? 'Manual income' : 'Manual output',
                staff: staffName
            });
            setStoreSuccess("Stock updated");
            const res = await axios.get(`${API_BASE}/inventory`);
            setInventory(res.data);
            setMovementsRefreshTrigger(t => t + 1);
        } catch (err) {
            setStoreError(err.response?.data?.error || "Update failed");
        }
    };

    // Update price
    const handleUpdatePrice = async (id, newPrice) => {
        setStoreError(""); setStoreSuccess("");
        const priceNum = Number(newPrice);
        if (isNaN(priceNum) || priceNum < 0) {
            setStoreError("Price must be a valid non-negative number");
            return;
        }
        // Skip when the value is unchanged since focus — a blur without an edit
        // shouldn't trigger a confirmation prompt.
        const original = priceEditOriginalRef.current[id];
        delete priceEditOriginalRef.current[id];
        if (original !== undefined && Number(original) === priceNum) return;
        const target = inventory.find(i => i._id === id);
        const ok = await askConfirm(`Update price for "${target?.item || 'this item'}"${original !== undefined ? ` from ${original}` : ''} to ${priceNum}?`, { confirmLabel: 'Update Price' });
        if (!ok) {
            // User declined — restore the displayed price to the original.
            if (original !== undefined) {
                setInventory(prev => prev.map(i => i._id === id ? { ...i, price: Number(original) } : i));
            }
            return;
        }
        try {
            await axios.patch(`${API_BASE}/inventory/${id}`, { price: priceNum });
            setStoreSuccess("Price updated");
            const res = await axios.get(`${API_BASE}/inventory`);
            setInventory(res.data);
        } catch (err) {
            setStoreError(err.response?.data?.error || "Update failed");
        }
    };

    // Add payment
    const handleAddPayment = async () => {
        setPaymentError(""); setPaymentSuccess("");
        const amountNum = Number(newPaymentAmount);
        if (isNaN(amountNum) || amountNum <= 0) {
            setPaymentError("Amount must be a positive number");
            return;
        }
        if (!newPaymentReference.trim()) {
            setPaymentError("Reference is required");
            return;
        }
        if (!(await askConfirm(`Record a payment of ${amountNum.toLocaleString()} for "${newPaymentReference.trim()}"?`, { confirmLabel: 'Record Payment' }))) return;
        try {
            const body = {
                amount: amountNum,
                reference: newPaymentReference.trim(),
                note: newPaymentNote.trim() || undefined,
                staff: staffName
            };
            if (newPaymentFile) {
                if (!newPaymentFile.type.startsWith('image/')) {
                    setPaymentError("Attachment must be an image");
                    return;
                }
                if (newPaymentFile.size > 5 * 1024 * 1024) {
                    setPaymentError("Attachment too large (max 5MB)");
                    return;
                }
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => reject(reader.error);
                    reader.readAsDataURL(newPaymentFile);
                });
                // dataUrl looks like "data:image/jpeg;base64,/9j/4AAQ..." — split off the header
                const commaIdx = dataUrl.indexOf(',');
                body.attachment = {
                    data: dataUrl.slice(commaIdx + 1),
                    contentType: newPaymentFile.type,
                    filename: newPaymentFile.name
                };
            }
            await axios.post(`${API_BASE}/payments`, body);
            setPaymentSuccess("Payment recorded successfully");
            setNewPaymentAmount(0);
            setNewPaymentReference("");
            setNewPaymentNote("");
            setNewPaymentFile(null);
            // Reset the native file input so the same filename can be picked again
            const fileInput = document.getElementById('paymentAttachmentInput');
            if (fileInput) fileInput.value = '';
            setPaymentsPage(1);
            setPaymentsFilterStartDate('');
            setPaymentsFilterEndDate('');
            // Bump the refresh trigger so the server-side table + analytics refetch.
            setPaymentsRefreshTrigger(t => t + 1);
        } catch (err) {
            setPaymentError(err.response?.data?.error || "Failed to record payment");
        }
    };


    const handleStartSession = async (dev) => {
        // Guard against duplicate POSTs: a fast double-tap on the device tile would
        // otherwise fire two concurrent requests. The server's partial unique index
        // is the authoritative defense, but this avoids the visible "Device is
        // already in use" alert from the loser of the race.
        if (startingDeviceNames.includes(dev.name)) return;
        if (activeSessions.some(s => s.device.name === dev.name)) return;
        if (!(await askConfirm(`Start a new session on "${dev.name}"?`, { confirmLabel: 'Start Session' }))) return;
        setStartingDeviceNames(prev => prev.includes(dev.name) ? prev : [...prev, dev.name]);
        try {
            const res = await axios.post(`${API_BASE}/sessions`, {
                deviceType: dev.name,
                startTime: new Date(),
                openedBy: staffName
            });
            const deviceRate = devices.find(d => d.name === dev.name)?.rate || rate;
            const newSession = {
                _id: res.data._id,
                device: dev,
                start: Date.now(),
                rate: deviceRate,
                openedBy: staffName,
                snacks: []
            };
            setActiveSessions(prev => [...prev, newSession]);
            // Seed default form state for this session's snack picker
            setSessionSnacks(prev => prev[newSession._id] ? prev : {
                ...prev,
                [newSession._id]: { snack: inventory[0]?.item || "", qty: 1, price: inventory[0]?.price || 10 }
            });
        } catch (err) {
            console.error('Failed to start session:', err);
            alert(err.response?.data?.error || 'Failed to start session. Please check your connection.');
        } finally {
            setStartingDeviceNames(prev => prev.filter(n => n !== dev.name));
        }
    };

    // Effective stock = on-hand minus what's already reserved in open sessions.
    // In-session snacks aren't decremented from inventory until handleEndSession,
    // so the raw stock number would let two sessions both reserve the last unit.
    const availableStock = (itemName) => {
        const inv = inventory.find(i => i.item === itemName);
        if (!inv) return 0;
        const reserved = activeSessions.reduce((sum, s) =>
            sum + (s.snacks || []).filter(sn => sn.item === itemName)
                .reduce((acc, sn) => acc + (sn.amount || 0), 0)
            , 0);
        return inv.stock - reserved;
    };

    // Remove an in-session snack before the session ends. Pure client-side: nothing
    // is posted to /sales until handleEndSession runs, so no server reversal is needed.
    const handleRemoveSessionSnack = async (sessionId, sidx) => {
        const session = activeSessions.find(s => s._id === sessionId);
        const snackName = session?.snacks?.[sidx]?.item || 'this item';
        if (!(await askConfirm(`Remove "${snackName}" from this sale?`, { confirmLabel: 'Remove', danger: true }))) return;
        setActiveSessions(prev => prev.map(s =>
            s._id === sessionId
                ? { ...s, snacks: (s.snacks || []).filter((_, i) => i !== sidx) }
                : s
        ));
    };

    const handleAddSnack = async (sessionId) => {
        const sessionSnack = sessionSnacks[sessionId] || { snack: "", qty: 1, price: 10 };
        if (!sessionSnack.snack) return;
        const qty = sessionSnack.qty || 1;

        // Block overselling: this snack plus everything reserved across other open
        // sessions must not exceed on-hand inventory.
        const inv = inventory.find(i => i.item === sessionSnack.snack);
        if (inv) {
            const available = availableStock(sessionSnack.snack);
            if (qty > available) {
                alert(`Not enough stock for ${sessionSnack.snack}. Available: ${Math.max(available, 0)}, requested: ${qty}.`);
                return;
            }
        }

        if (!(await askConfirm(`Add ${qty} × "${sessionSnack.snack}" (${((sessionSnack.price || 10) * qty).toLocaleString()}) to this sale?`, { confirmLabel: 'Add Sale' }))) return;

        const snackSale = {
            category: 'Snack',
            item: sessionSnack.snack,
            amount: qty,
            price: (sessionSnack.price || 10) * qty
        };

        const session = activeSessions.find(s => s._id === sessionId);
        if (session) {
            setActiveSessions(prev => prev.map(s =>
                s._id === sessionId ? { ...s, snacks: [...(s.snacks || []), snackSale] } : s
            ));
        } else {
            handleDirectSnackSale(snackSale);
        }
    };

    const handleDirectSnackSale = async (snackSale) => {
        try {
            await axios.post(`${API_BASE}/sales`, { ...snackSale, timestamp: new Date(), staff: staffName });
            // Update inventory stock
            const inventoryItem = inventory.find(inv => inv.item === snackSale.item);
            if (inventoryItem) {
                await axios.patch(`${API_BASE}/inventory/${inventoryItem._id}`, {
                    stock: -snackSale.amount,
                    note: 'Snack sale',
                    staff: staffName
                });
                // Refresh inventory
                const res = await axios.get(`${API_BASE}/inventory`);
                setInventory(res.data);
                setMovementsRefreshTrigger(t => t + 1);
            }
            setSnackSuccess("Snack sale recorded successfully!");
            setTimeout(() => setSnackSuccess(""), 3000);
        } catch (err) {
            setSnackError("Failed to record snack sale");
            setTimeout(() => setSnackError(""), 3000);
        }
    };

    const handleEndSession = async (sessionId) => {
        const session = activeSessions.find(s => s._id === sessionId);
        if (!session) return;
        if (!(await askConfirm(`End the session on "${session.device.name}" and charge the customer?`, { confirmLabel: 'End Session', danger: true }))) return;
        setEndingSessionIds(prev => prev.includes(sessionId) ? prev : [...prev, sessionId]);
        const end = Date.now();
        const durationMin = Math.round((end - session.start) / 60000);
        const charge = Math.round(((durationMin / 60) * session.rate) / 1000) * 1000;
        try {
            await axios.patch(`${API_BASE}/sessions/${session._id}`, {
                endTime: new Date(end),
                duration: durationMin,
                charge: charge,
                closedBy: staffName
            });

            // The device-time Sale row is gated on charge >= 1000 (sessions that
            // round to zero time are treated as free). Snacks are independent and
            // must always post — staff have already handed them to the customer.
            const allSales = [];
            if (charge >= 1000) {
                allSales.push({
                    category: session.device.type,
                    item: session.device.name,
                    amount: 1,
                    price: charge,
                    timestamp: new Date(end)
                });
            }
            for (const snack of (session.snacks || [])) {
                allSales.push({ ...snack, timestamp: new Date(end) });
            }

            if (allSales.length > 0) {
                for (const sale of allSales) {
                    await axios.post(`${API_BASE}/sales`, { ...sale, staff: staffName });
                    if (sale.category === 'Snack') {
                        const inventoryItem = inventory.find(inv => inv.item === sale.item);
                        if (inventoryItem) {
                            await axios.patch(`${API_BASE}/inventory/${inventoryItem._id}`, {
                                stock: -sale.amount,
                                note: 'Snack sale',
                                staff: staffName
                            });
                            setMovementsRefreshTrigger(t => t + 1);
                        }
                    }
                }
                const updatedInventory = await axios.get(`${API_BASE}/inventory`);
                setInventory(updatedInventory.data);
                setSaleSummaries(prev => [...prev, {
                    id: `${session._id}-${end}`,
                    items: allSales,
                    total: allSales.reduce((sum, s) => sum + s.price, 0),
                    device: session.device.name,
                    openedBy: session.openedBy || '',
                    closedBy: staffName
                }]);
            }

            setActiveSessions(prev => prev.filter(s => s._id !== sessionId));
            setSessionSnacks(prev => {
                if (!(sessionId in prev)) return prev;
                const next = { ...prev };
                delete next[sessionId];
                return next;
            });
        } catch (err) {
            console.error('Failed to end session:', err);
            alert('Failed to end session. Please try again.');
        } finally {
            setEndingSessionIds(prev => prev.filter(id => id !== sessionId));
        }
    };

    // Elapsed-time tick: only run while the Sessions tab is visible and at least
    // one session is open. We just bump a counter to force a re-render so the
    // "Elapsed: X min" display refreshes — activeSessions itself is untouched.
    const [, setElapsedTick] = useState(0);
    useEffect(() => {
        if (!(showSession && activeSessions.length > 0)) return;
        const timer = setInterval(() => setElapsedTick(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, [showSession, activeSessions.length]);

    // Persist rate changes
    useEffect(() => {
        localStorage.setItem('rate', rate);
    }, [rate]);

    // Persist devices changes
    useEffect(() => {
        localStorage.setItem('devices', JSON.stringify(devices));
    }, [devices]);

    // Export the full filtered set (all pages) to CSV. With server-side paging the
    // client no longer holds every row, so fetch them all with length=-1 honouring
    // the current filters, then format identically to the on-screen rows.
    const exportToCSV = async () => {
        const headers = ['Date', 'Time', 'Category', 'Item', 'Amount', 'Price', 'Staff'];
        let rows = [];
        try {
            const res = await axios.get(`${API_BASE}/sales/datatable`, {
                params: { draw: 0, start: 0, length: -1, ...buildSalesFilterParams() }
            });
            rows = (res.data.data || []).map(r => {
                const formatted = formatSalesRow(r);
                // Use the raw numeric price for the CSV (formatSalesRow localises it for display).
                formatted[5] = r.price;
                return formatted;
            });
        } catch (err) {
            console.error('Error exporting sales:', err);
            return;
        }
        const csvContent = [headers, ...rows].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'reports.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportInventoryToCSV = (items = inventory) => {
        const headers = ['Item', 'Stock', 'Price'];
        const data = items.map(item => [
            item.item,
            item.stock,
            item.price
        ]);
        const csvContent = [headers, ...data].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'inventory.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export the full filtered payment set (all pages) to CSV via the server-side
    // endpoint with length=-1, honouring the current date filter.
    const exportPaymentsToCSV = async () => {
        const headers = ['Date', 'Time', 'Amount', 'Reference', 'Note', 'Staff'];
        let data = [];
        try {
            const res = await axios.get(`${API_BASE}/payments/datatable`, {
                params: { draw: 0, start: 0, length: -1, ...buildPaymentsFilterParams() }
            });
            data = (res.data.data || []).map(payment => [
                new Date(payment.timestamp).toLocaleDateString(),
                new Date(payment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                payment.amount,
                payment.reference,
                payment.note || '',
                payment.staff || ''
            ]);
        } catch (err) {
            console.error('Error exporting payments:', err);
            return;
        }
        const csvContent = [headers, ...data].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'payments.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // The Reports/Sales, Payments and Stock Movements tables are all server-side now:
    // rows are fetched per page by their *Ajax loaders and formatted by format*Row.
    // No full-dataset memos are kept here.

    // Reports UI
    const renderReports = () => {
        const isMobile = window.innerWidth < 600;

        const clearFilters = () => {
            setFilterCategory('');
            setFilterStartDate('');
            setFilterEndDate('');
            setFilterStartTime('');
            setFilterEndTime('');
            setCurrentPage(1);
            // Clearing state triggers the filter effect, which reloads the table.
        };

        return (
            <div style={{ maxWidth: 1200, margin: isMobile ? '1rem auto' : '2rem auto', padding: isMobile ? 20 : 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                <h2 style={{ textAlign: 'center', marginBottom: 30 }}>Reports Dashboard</h2>
                <button onClick={() => {
                    setShowReports(false);
                    setShowSession(true);
                    setCurrentPage(1);
                    localStorage.setItem('showReports', 'false');
                    localStorage.setItem('showSession', 'true');
                }} style={{ marginBottom: 20, padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Back</button>

                <div style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontWeight: 600 }}>Category:
                        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setCurrentPage(1); }} style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }}>
                            <option value="">All</option>
                            <option value="PS4">PS4</option>
                            <option value="PS5">PS5</option>
                            <option value="PC">PC</option>
                            <option value="Bilardo">Bilardo</option>
                            <option value="Snack">Snack</option>
                        </select>
                    </label>
                    <label style={{ fontWeight: 600 }}>Start Date & Time:
                        <input type="date" value={filterStartDate} onChange={e => { setFilterStartDate(e.target.value); setCurrentPage(1); }} style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                        <input type="time" value={filterStartTime} onChange={e => { setFilterStartTime(e.target.value); setCurrentPage(1); }} style={{ marginLeft: 5, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                    </label>
                    <label style={{ fontWeight: 600 }}>End Date & Time:
                        <input type="date" value={filterEndDate} onChange={e => { setFilterEndDate(e.target.value); setCurrentPage(1); }} style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                        <input type="time" value={filterEndTime} onChange={e => { setFilterEndTime(e.target.value); setCurrentPage(1); }} style={{ marginLeft: 5, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                    </label>
                    <button onClick={clearFilters} style={{ padding: 10, borderRadius: 8, background: '#ff5252', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Clear Filters</button>
                    <button onClick={exportToCSV} style={{ padding: 10, borderRadius: 8, background: '#00e676', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginLeft: 10 }}>Export to Excel</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <DataTable
                        ref={salesTableRef}
                        columns={[
                            { title: 'Date' },
                            { title: 'Time' },
                            { title: 'Category' },
                            { title: 'Item' },
                            { title: 'Amount' },
                            { title: 'Price' },
                            { title: 'Staff' }
                        ]}
                        options={{
                            serverSide: true,
                            processing: true,
                            ajax: salesAjax,
                            pageLength: 10,
                            lengthMenu: [10, 25, 50, 100],
                            order: [[0, 'desc']],
                            language: { emptyTable: 'No sales records to display' }
                        }}
                        className="display"
                    />
                </div>
                {(filterCategory || filterStartDate || filterEndDate) && totalRecords > 0 && (
                    <div style={{ textAlign: 'center', margin: '20px 0', fontSize: 20, fontWeight: 700, color: '#00e676' }}>
                        Total Price for Filtered Records: {Math.round(filteredTotalPrice).toLocaleString()} ل.س
                    </div>
                )}
                {/* Income Analytics Section */}
                <div style={{ marginBottom: 30, padding: 20, background: 'rgba(0,0,0,0.1)', borderRadius: 12 }}>
                    <h3 style={{ textAlign: 'center', marginBottom: 20 }}>Income Analytics</h3>

                    {/* Time Period Analytics */}
                    <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 20, flexWrap: 'wrap', gap: 20 }}>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>Today</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(incomeAnalytics.day.totalIncome).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{incomeAnalytics.day.count} transactions</p>
                        </div>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>This Month</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(incomeAnalytics.month.totalIncome).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{incomeAnalytics.month.count} transactions</p>
                        </div>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>This Year</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(incomeAnalytics.year.totalIncome).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{incomeAnalytics.year.count} transactions</p>
                        </div>
                    </div>

                    {/* Device Type Analytics */}
                    <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 15 }}>
                        {Object.entries(incomeAnalytics.devices).map(([device, data]) => (
                            <div key={device} style={{ textAlign: 'center', padding: 15, background: 'rgba(0, 229, 118, 0.1)', borderRadius: 8, minWidth: 120 }}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>{device}</h4>
                                <bdi style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#00e676' }}>{Math.round(data.totalIncome).toLocaleString()} ل.س</bdi>
                                <p style={{ margin: 0, fontSize: 11, opacity: 0.8 }}>{device === 'Snack' ? data.count + ' sales' : data.count + ' sessions'}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // MongoDB ObjectIds embed the document's creation time in their first 4 bytes
    // (8 hex chars = unix seconds). Lets us derive "when was this added?" without
    // touching the schema or backfilling existing docs.
    const objectIdToDate = (id) => {
        if (!id || typeof id !== 'string' || id.length < 8) return null;
        const seconds = parseInt(id.substring(0, 8), 16);
        if (Number.isNaN(seconds)) return null;
        return new Date(seconds * 1000);
    };

    // Inventory stats + filter + sort, memoized so the stat tiles and table don't
    // recompute on unrelated re-renders. Status thresholds: 0 = out, ≤lowStockAlert = low.
    const inventoryStats = useMemo(() => {
        const total = inventory.length;
        const out = inventory.filter(i => (i.stock || 0) <= 0).length;
        const low = inventory.filter(i => i.stock > 0 && i.stock <= (i.lowStockAlert || 5)).length;
        return { total, inStock: total - out - low, low, out };
    }, [inventory]);

    const filteredInventory = useMemo(() => {
        let result = inventory;
        const q = inventorySearch.trim().toLowerCase();
        if (q) result = result.filter(i => (i.item || '').toLowerCase().includes(q));
        if (inventoryStockFilter === 'in') {
            result = result.filter(i => i.stock > (i.lowStockAlert || 5));
        } else if (inventoryStockFilter === 'low') {
            result = result.filter(i => i.stock > 0 && i.stock <= (i.lowStockAlert || 5));
        } else if (inventoryStockFilter === 'out') {
            result = result.filter(i => (i.stock || 0) <= 0);
        }
        // Date range filter: by item creation time (decoded from ObjectId)
        if (inventoryStartDate || inventoryEndDate) {
            const start = inventoryStartDate ? new Date(`${inventoryStartDate}T00:00:00`) : null;
            const end = inventoryEndDate ? new Date(`${inventoryEndDate}T23:59:59.999`) : null;
            result = result.filter(i => {
                const created = objectIdToDate(i._id);
                if (!created) return true; // keep items we can't decode rather than silently drop
                if (start && created < start) return false;
                if (end && created > end) return false;
                return true;
            });
        }
        const sorted = [...result].sort((a, b) => {
            let cmp = 0;
            if (inventorySortBy === 'item') cmp = (a.item || '').localeCompare(b.item || '');
            else if (inventorySortBy === 'stock') cmp = (a.stock || 0) - (b.stock || 0);
            else if (inventorySortBy === 'price') cmp = (a.price || 0) - (b.price || 0);
            else if (inventorySortBy === 'created') {
                const ta = objectIdToDate(a._id)?.getTime() || 0;
                const tb = objectIdToDate(b._id)?.getTime() || 0;
                cmp = ta - tb;
            }
            return inventorySortDir === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [inventory, inventorySearch, inventoryStockFilter, inventorySortBy, inventorySortDir, inventoryStartDate, inventoryEndDate]);

    const getStockStatus = (inv) => {
        if ((inv.stock || 0) <= 0) return { label: 'Out', color: '#ff5252', bg: 'rgba(255,82,82,0.18)' };
        if (inv.stock <= (inv.lowStockAlert || 5)) return { label: 'Low', color: '#ffa726', bg: 'rgba(255,167,38,0.18)' };
        return { label: 'In Stock', color: '#00e676', bg: 'rgba(0,229,118,0.18)' };
    };

    // Format one server stock-movement row for the DataTable. Cells are HTML strings
    // (DataTables renders them as HTML by default), so Type and Quantity are color-coded inline.
    const formatMovementRow = (m) => {
        const isIncome = m.delta > 0;
        const typeBadge = isIncome
            ? '<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:rgba(0,229,118,0.18);color:#00e676;font-weight:700;font-size:12px">Income</span>'
            : '<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:rgba(255,82,82,0.18);color:#ff5252;font-weight:700;font-size:12px">Output</span>';
        const qtyDisplay = isIncome
            ? `<span style="color:#00e676;font-weight:700">+${m.delta}</span>`
            : `<span style="color:#ff5252;font-weight:700">${m.delta}</span>`;
        return [
            new Date(m.timestamp).toLocaleDateString(),
            new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            m.itemName || '—',
            typeBadge,
            qtyDisplay,
            m.note || '-',
            m.staff || '-'
        ];
    };

    // Store Management UI
    const renderStore = () => {
        const isMobile = window.innerWidth < 600;

        const handleSortClick = (col) => {
            if (inventorySortBy === col) {
                setInventorySortDir(inventorySortDir === 'asc' ? 'desc' : 'asc');
            } else {
                setInventorySortBy(col);
                setInventorySortDir('asc');
            }
        };
        const sortIndicator = (col) => {
            if (inventorySortBy !== col) return ' ';
            return inventorySortDir === 'asc' ? ' ▲' : ' ▼';
        };
        // Click a stat tile to filter; click again (or another) to toggle.
        const toggleStockFilter = (filter) => {
            setInventoryStockFilter(prev => prev === filter ? 'all' : filter);
        };

        const statTile = (label, value, color, filterKey) => {
            const active = inventoryStockFilter === filterKey;
            return (
                <button
                    type="button"
                    onClick={() => filterKey ? toggleStockFilter(filterKey) : null}
                    disabled={!filterKey}
                    style={{
                        flex: '1 1 140px',
                        padding: '14px 12px',
                        background: active ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.04)',
                        borderRadius: 10,
                        border: active ? `2px solid ${color}` : '2px solid transparent',
                        cursor: filterKey ? 'pointer' : 'default',
                        textAlign: 'center',
                        color: '#fff',
                        transition: 'background 0.15s, border-color 0.15s',
                    }}
                >
                    <div style={{ fontSize: 12, opacity: 0.8, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
                </button>
            );
        };

        return (
            <div style={{ maxWidth: 1000, margin: isMobile ? '1rem auto' : '2rem auto', padding: isMobile ? 20 : 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                <h2 style={{ textAlign: 'center', marginBottom: 30, fontSize: isMobile ? '1.5rem' : '2rem' }}>Store Management</h2>
                <button onClick={() => {
                    setShowStore(false);
                    setShowSession(true);
                    localStorage.setItem('showStore', 'false');
                    localStorage.setItem('showSession', 'true');
                }} style={{ marginBottom: 20, padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Back</button>
                <div style={{ marginBottom: 30 }}>
                    <h3>Add New Item</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <label style={{ fontWeight: 600 }}>
                            Item Name:
                            <input value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="Enter item name" style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 120 : 150 }} />
                        </label>
                        <label style={{ fontWeight: 600 }}>
                            Initial Stock:
                            <input type="number" value={newItemStock} onChange={e => setNewItemStock(e.target.value)} placeholder="0" style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 80 : 100 }} />
                        </label>
                        <label style={{ fontWeight: 600 }}>
                            Price:
                            <input type="number" step="1" value={newItemPrice} onChange={e => setNewItemPrice(e.target.value)} placeholder="0.00" style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 70 : 80 }} />
                        </label>
                        <button onClick={handleAddItem} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Add Item</button>
                    </div>
                </div>
                <h3 style={{ marginBottom: 12 }}>Inventory</h3>

                {/* Stat tiles — also act as quick stock-status filters */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                    {statTile('Total Items', inventoryStats.total, '#9c88ff', null)}
                    {statTile('In Stock', inventoryStats.inStock, '#00e676', 'in')}
                    {statTile('Low Stock', inventoryStats.low, '#ffa726', 'low')}
                    {statTile('Out of Stock', inventoryStats.out, '#ff5252', 'out')}
                </div>

                {/* Filter bar */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14, padding: 12, background: 'rgba(0,0,0,0.15)', borderRadius: 10 }}>
                    <input
                        type="text"
                        value={inventorySearch}
                        onChange={e => setInventorySearch(e.target.value)}
                        placeholder="Search item name..."
                        style={{ flex: '1 1 180px', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}
                    />
                    <select
                        value={inventoryStockFilter}
                        onChange={e => setInventoryStockFilter(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}
                    >
                        <option value="all">All statuses</option>
                        <option value="in">In Stock</option>
                        <option value="low">Low Stock</option>
                        <option value="out">Out of Stock</option>
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.85 }}>
                        Added from
                        <input
                            type="date"
                            value={inventoryStartDate}
                            onChange={e => setInventoryStartDate(e.target.value)}
                            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}
                        />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.85 }}>
                        to
                        <input
                            type="date"
                            value={inventoryEndDate}
                            onChange={e => setInventoryEndDate(e.target.value)}
                            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}
                        />
                    </label>
                    <button
                        onClick={() => {
                            setInventorySearch('');
                            setInventoryStockFilter('all');
                            setInventoryStartDate('');
                            setInventoryEndDate('');
                            setInventorySortBy('item');
                            setInventorySortDir('asc');
                        }}
                        style={{ padding: '8px 14px', borderRadius: 6, background: '#ff5252', color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
                    >
                        Clear Filters
                    </button>
                    <button
                        onClick={() => exportInventoryToCSV(filteredInventory)}
                        style={{ padding: '8px 14px', borderRadius: 6, background: '#00e676', color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
                    >
                        Export to CSV
                    </button>
                    <span style={{ marginLeft: 'auto', fontSize: 13, opacity: 0.85 }}>
                        Showing {filteredInventory.length} of {inventoryStats.total}
                    </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', background: 'rgba(0,0,0,0.07)', borderRadius: 8, marginBottom: 20, minWidth: 700, borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                            <tr style={{ background: 'rgba(0,0,0,0.18)' }}>
                                <th style={{ padding: 10, textAlign: 'left' }}>Status</th>
                                <th onClick={() => handleSortClick('item')} style={{ padding: 10, textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>Item{sortIndicator('item')}</th>
                                <th onClick={() => handleSortClick('stock')} style={{ padding: 10, cursor: 'pointer', userSelect: 'none' }}>Stock{sortIndicator('stock')}</th>
                                <th onClick={() => handleSortClick('price')} style={{ padding: 10, cursor: 'pointer', userSelect: 'none' }}>Price{sortIndicator('price')}</th>
                                <th onClick={() => handleSortClick('created')} style={{ padding: 10, cursor: 'pointer', userSelect: 'none' }}>Added{sortIndicator('created')}</th>
                                <th style={{ padding: 10 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInventory.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ padding: 24, textAlign: 'center', opacity: 0.7 }}>
                                        {inventory.length === 0 ? 'No inventory items yet — add one above.' : 'No items match the current filters.'}
                                    </td>
                                </tr>
                            )}
                            {filteredInventory.map((inv, idx) => {
                                const status = getStockStatus(inv);
                                return (
                                    <tr key={inv._id} style={{ background: idx % 2 === 0 ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.10)' }}>
                                        <td style={{ padding: 10 }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: status.bg, color: status.color, fontSize: 12, fontWeight: 700 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.color }} />
                                                {status.label}
                                            </span>
                                        </td>
                                        <td style={{ padding: 10, fontWeight: 600 }}>{inv.item}</td>
                                        <td style={{ padding: 10, textAlign: 'center' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={stockInputs[inv._id] !== undefined ? stockInputs[inv._id] : inv.stock}
                                                onChange={e => setStockInputs(prev => ({ ...prev, [inv._id]: e.target.value }))}
                                                onBlur={() => commitStockEdit(inv)}
                                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                                style={{ padding: 4, borderRadius: 4, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14, width: 90, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                            />
                                        </td>
                                        <td style={{ padding: 10, textAlign: 'center' }}>
                                            <input
                                                type="number"
                                                step="1"
                                                value={inv.price}
                                                onFocus={() => { priceEditOriginalRef.current[inv._id] = inv.price; }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    const numVal = val === '' ? 0 : parseFloat(val);
                                                    const updated = inventory.map(i => i._id === inv._id ? { ...i, price: isNaN(numVal) ? 0 : numVal } : i);
                                                    setInventory(updated);
                                                }}
                                                onBlur={() => handleUpdatePrice(inv._id, inv.price)}
                                                style={{ padding: 4, borderRadius: 4, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14, width: 90, textAlign: 'right' }}
                                            />
                                        </td>
                                        <td style={{ padding: 10, textAlign: 'center', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', opacity: 0.9 }}>
                                            {(() => {
                                                const d = objectIdToDate(inv._id);
                                                return d ? d.toLocaleDateString() : '—';
                                            })()}
                                        </td>
                                        <td style={{ padding: 10, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleRemoveItem(inv._id)}
                                                style={{ padding: '6px 10px', borderRadius: 6, background: '#ff5252', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}
                                            >Remove</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Stock Movements Log — append-only audit of every stock change */}
                <div style={{ marginTop: 36 }}>
                    <h3 style={{ marginBottom: 4 }}>Stock Movements Log</h3>
                    <p style={{ margin: '0 0 14px 0', fontSize: 13, opacity: 0.7 }}>
                        Every income (added units) and output (sold/removed units) recorded automatically.
                    </p>

                    {/* Totals tiles */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                        <div style={{ flex: '1 1 140px', padding: 14, background: 'rgba(0,229,118,0.10)', border: '1px solid rgba(0,229,118,0.25)', borderRadius: 10, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase' }}>Total Income</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: '#00e676', marginTop: 4 }}>+{stockMovementsTotals.totalIncome}</div>
                        </div>
                        <div style={{ flex: '1 1 140px', padding: 14, background: 'rgba(255,82,82,0.10)', border: '1px solid rgba(255,82,82,0.25)', borderRadius: 10, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase' }}>Total Output</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: '#ff5252', marginTop: 4 }}>−{stockMovementsTotals.totalOutput}</div>
                        </div>
                        <div style={{ flex: '1 1 140px', padding: 14, background: stockMovementsTotals.net >= 0 ? 'rgba(0,229,118,0.16)' : 'rgba(255,82,82,0.16)', border: `1px solid ${stockMovementsTotals.net >= 0 ? 'rgba(0,229,118,0.30)' : 'rgba(255,82,82,0.30)'}`, borderRadius: 10, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase' }}>Net Change</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: stockMovementsTotals.net >= 0 ? '#00e676' : '#ff5252', marginTop: 4 }}>{stockMovementsTotals.net > 0 ? '+' : ''}{stockMovementsTotals.net}</div>
                        </div>
                        <div style={{ flex: '1 1 140px', padding: 14, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase' }}>Records</div>
                            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{stockMovementsTotals.count}</div>
                        </div>
                    </div>

                    {/* Movement filters */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14, padding: 12, background: 'rgba(0,0,0,0.15)', borderRadius: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.85 }}>
                            From
                            <input type="date" value={movementsStartDate} onChange={e => setMovementsStartDate(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }} />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.85 }}>
                            To
                            <input type="date" value={movementsEndDate} onChange={e => setMovementsEndDate(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }} />
                        </label>
                        <select value={movementsItemFilter} onChange={e => setMovementsItemFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}>
                            <option value="">All items</option>
                            {inventory.map(inv => <option key={inv._id} value={inv._id}>{inv.item}</option>)}
                        </select>
                        <select value={movementsTypeFilter} onChange={e => setMovementsTypeFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14 }}>
                            <option value="all">All types</option>
                            <option value="income">Income only</option>
                            <option value="output">Output only</option>
                        </select>
                        <button
                            onClick={() => { setMovementsStartDate(''); setMovementsEndDate(''); setMovementsItemFilter(''); setMovementsTypeFilter('all'); }}
                            style={{ padding: '8px 14px', borderRadius: 6, background: '#ff5252', color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
                        >
                            Clear Filters
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <DataTable
                            ref={movementsTableRef}
                            columns={[
                                { title: 'Date' },
                                { title: 'Time' },
                                { title: 'Item' },
                                { title: 'Type', orderable: false },
                                { title: 'Quantity' },
                                { title: 'Note' },
                                { title: 'Staff' }
                            ]}
                            options={{
                                serverSide: true,
                                processing: true,
                                ajax: movementsAjax,
                                pageLength: 10,
                                lengthMenu: [10, 25, 50, 100],
                                order: [[0, 'desc']],
                                language: { emptyTable: 'No stock movements recorded for this filter' }
                            }}
                            className="display"
                        />
                    </div>
                </div>

                {storeError && <div style={{ color: '#ff5252', marginTop: 10, textAlign: 'center' }}>{storeError}</div>}
                {storeSuccess && <div style={{ color: '#00e676', marginTop: 10, textAlign: 'center' }}>{storeSuccess}</div>}
            </div>
        );
    };

    // Payments UI
    const renderPayments = () => {
        const isMobile = window.innerWidth < 600;

        // Labels for the Net Profit scope groups
        const currentMonthLabel = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(new Date());
        const rangeLabelParts = [];
        if (paymentsFilterStartDate) {
            rangeLabelParts.push(`from ${paymentsFilterStartDate}${paymentsFilterStartTime ? ' ' + paymentsFilterStartTime : ''}`);
        }
        if (paymentsFilterEndDate) {
            rangeLabelParts.push(`to ${paymentsFilterEndDate}${paymentsFilterEndTime ? ' ' + paymentsFilterEndTime : ''}`);
        }
        const rangeLabel = rangeLabelParts.join(' ');

        // One scope group within the Net Profit Report (All Time / This Month / Filtered Range)
        const renderNetProfitGroup = (label, sublabel, data, accentColor) => {
            const netPositive = data.netProfit >= 0;
            return (
                <div key={label} style={{ marginBottom: 16, padding: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 10, borderLeft: `4px solid ${accentColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                        <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{label}</h4>
                        {sublabel && <span style={{ fontSize: 13, opacity: 0.7 }}>{sublabel}</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                        <div style={{ textAlign: 'center', padding: '12px 10px', background: 'rgba(0, 229, 118, 0.10)', borderRadius: 8, border: '1px solid rgba(0,229,118,0.20)' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4, letterSpacing: 0.3 }}>INCOME</div>
                            <bdi style={{ fontSize: 20, fontWeight: 700, color: '#00e676' }}>{Math.round(data.totalIncome).toLocaleString()} ل.س</bdi>
                        </div>
                        <div style={{ textAlign: 'center', padding: '12px 10px', background: 'rgba(255, 82, 82, 0.10)', borderRadius: 8, border: '1px solid rgba(255,82,82,0.20)' }}>
                            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4, letterSpacing: 0.3 }}>EXPENSES</div>
                            <bdi style={{ fontSize: 20, fontWeight: 700, color: '#ff5252' }}>{Math.round(data.totalPayments).toLocaleString()} ل.س</bdi>
                        </div>
                        <div style={{ textAlign: 'center', padding: '12px 10px', background: netPositive ? 'rgba(0, 229, 118, 0.16)' : 'rgba(255, 82, 82, 0.16)', borderRadius: 8, border: `1px solid ${netPositive ? 'rgba(0,229,118,0.30)' : 'rgba(255,82,82,0.30)'}` }}>
                            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4, letterSpacing: 0.3 }}>NET PROFIT</div>
                            <bdi style={{ fontSize: 22, fontWeight: 800, color: netPositive ? '#00e676' : '#ff5252' }}>{Math.round(data.netProfit).toLocaleString()} ل.س</bdi>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div style={{ maxWidth: 1000, margin: isMobile ? '1rem auto' : '2rem auto', padding: isMobile ? 20 : 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                <h2 style={{ textAlign: 'center', marginBottom: 30, fontSize: isMobile ? '1.5rem' : '2rem' }}>Payments Management</h2>
                <button onClick={() => {
                    setShowPayments(false);
                    setShowSession(true);
                    localStorage.setItem('showPayments', 'false');
                    localStorage.setItem('showSession', 'true');
                }} style={{ marginBottom: 20, padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Back</button>

                <h3>Payment History</h3>
                <div style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontWeight: 600 }}>Start Date & Time:
                        <input type="date" value={paymentsFilterStartDate} onChange={e => { setPaymentsFilterStartDate(e.target.value); setPaymentsPage(1); }} style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                        <input type="time" value={paymentsFilterStartTime} onChange={e => { setPaymentsFilterStartTime(e.target.value); setPaymentsPage(1); }} style={{ marginLeft: 5, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                    </label>
                    <label style={{ fontWeight: 600 }}>End Date & Time:
                        <input type="date" value={paymentsFilterEndDate} onChange={e => { setPaymentsFilterEndDate(e.target.value); setPaymentsPage(1); }} style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                        <input type="time" value={paymentsFilterEndTime} onChange={e => { setPaymentsFilterEndTime(e.target.value); setPaymentsPage(1); }} style={{ marginLeft: 5, padding: 8, borderRadius: 6, border: 'none', fontSize: 16 }} />
                    </label>
                    <button onClick={() => { setPaymentsFilterStartDate(''); setPaymentsFilterEndDate(''); setPaymentsFilterStartTime(''); setPaymentsFilterEndTime(''); setPaymentsPage(1); }} style={{ padding: 10, borderRadius: 8, background: '#ff5252', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Clear Filters</button>
                    <button onClick={exportPaymentsToCSV} style={{ padding: 10, borderRadius: 8, background: '#00e676', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Export Payments to Excel</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <DataTable
                        ref={paymentsTableRef}
                        columns={[
                            { title: 'Date' },
                            { title: 'Time' },
                            { title: 'Amount' },
                            { title: 'Reference' },
                            { title: 'Note' },
                            { title: 'Staff' },
                            { title: 'Invoice', orderable: false, searchable: false }
                        ]}
                        options={{
                            serverSide: true,
                            processing: true,
                            ajax: paymentsAjax,
                            pageLength: 10,
                            lengthMenu: [10, 25, 50, 100],
                            order: [[0, 'desc']],
                            language: { emptyTable: 'No payments to display' }
                        }}
                        className="display"
                    />
                </div>
                {(paymentsFilterStartDate || paymentsFilterEndDate) && paymentsTotal > 0 && (
                    <div style={{ textAlign: 'center', margin: '20px 0', fontSize: 20, fontWeight: 700, color: '#ff5252' }}>
                        Total Price for Filtered Records: {Math.round(paymentsFilteredTotal).toLocaleString()} ل.س
                    </div>
                )}
                <div style={{ marginBottom: 30 }}>
                    <h3>Record New Payment</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <label style={{ fontWeight: 600 }}>
                            Amount:
                            <input type="number" step="1" value={newPaymentAmount} onChange={e => setNewPaymentAmount(e.target.value)} placeholder="0.00" style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 80 : 100 }} />
                        </label>
                        <label style={{ fontWeight: 600 }}>
                            Reference:
                            <input value={newPaymentReference} onChange={e => setNewPaymentReference(e.target.value)} placeholder="Sale/Payment" required style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 120 : 150 }} />
                        </label>
                        <label style={{ fontWeight: 600 }}>
                            Note:
                            <input value={newPaymentNote} onChange={e => setNewPaymentNote(e.target.value)} placeholder="Optional note" style={{ marginLeft: 10, padding: 8, borderRadius: 6, border: 'none', fontSize: 16, width: isMobile ? 120 : 150 }} />
                        </label>
                        <label style={{ fontWeight: 600 }}>
                            Invoice (optional):
                            <input
                                id="paymentAttachmentInput"
                                type="file"
                                accept="image/*"
                                onChange={e => setNewPaymentFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 14, color: '#fff', background: 'rgba(0,0,0,0.25)' }}
                            />
                            {newPaymentFile && (
                                <span style={{ marginLeft: 8, fontSize: 13, opacity: 0.85 }}>{newPaymentFile.name}</span>
                            )}
                        </label>
                        <button onClick={handleAddPayment} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Record Payment</button>
                    </div>
                </div>
                {/* Payment Analytics Section */}
                <div style={{ marginBottom: 30, padding: 20, background: 'rgba(0,0,0,0.1)', borderRadius: 12 }}>
                    <h3 style={{ textAlign: 'center', marginBottom: 20 }}>Payment Analytics</h3>

                    {/* Time Period Analytics */}
                    <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 20, flexWrap: 'wrap', gap: 20 }}>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>Total</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(paymentAnalytics.total.totalAmount).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{paymentAnalytics.total.total} payments</p>
                        </div>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>This Month</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(paymentAnalytics.month.totalAmount).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{paymentAnalytics.month.total} payments</p>
                        </div>
                        <div style={{ textAlign: 'center', padding: 15, background: 'rgba(42, 82, 152, 0.2)', borderRadius: 8, minWidth: 120 }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: 16 }}>This Year</h4>
                            <bdi style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#00e676' }}>{Math.round(paymentAnalytics.year.totalAmount).toLocaleString()} ل.س</bdi>
                            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>{paymentAnalytics.year.total} payments</p>
                        </div>
                    </div>
                </div>
                {/* Net Profit Section — stacked scopes: All Time / This Month / Filtered Range */}
                {(netProfitData || netProfitMonthData || netProfitRangeData) && (
                    <div style={{ marginBottom: 30, padding: 22, background: 'rgba(0,0,0,0.18)', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
                        <h3 style={{ textAlign: 'center', marginBottom: 22, fontSize: 22, letterSpacing: 0.5 }}>Net Profit Report</h3>
                        {netProfitRangeData && renderNetProfitGroup('Filtered Range', rangeLabel, netProfitRangeData, '#ff9800')}
                        {netProfitMonthData && renderNetProfitGroup('This Month', currentMonthLabel, netProfitMonthData, '#2a5298')}
                        {netProfitData && renderNetProfitGroup('All Time', null, netProfitData, '#9c88ff')}
                    </div>
                )}
                {paymentError && <div style={{ color: '#ff5252', marginTop: 10, textAlign: 'center' }}>{paymentError}</div>}
                {paymentSuccess && <div style={{ color: '#00e676', marginTop: 10, textAlign: 'center' }}>{paymentSuccess}</div>}
            </div>
        );
    };

    // UI
    return (
        <div style={{ fontFamily: 'Montserrat, Arial', background: 'linear-gradient(135deg, #211936 0%, #2f2549ff 100%)', minHeight: '100vh', color: '#fff' }}>
            <header style={{ textAlign: 'center', padding: '2rem 0 1rem 0', background: '#211936' }}>
                <img src="/assets/spacex-logo.png" alt="SpaceX" style={{ height: 140, marginBottom: 10 }} />
                <h1 style={{ fontSize: '2.5rem', fontWeight: 700, letterSpacing: 2, color: 'white' }}>SpaceX Gaming Cafe</h1>
                <p style={{ fontSize: '1.2rem', opacity: 0.8 }}>Multi-session management & monitoring</p>
                {authToken && (
                    <div style={{ marginTop: 10, fontSize: 18, fontWeight: 600, color: '#fff', opacity: 0.85 }}>
                        Welcome, {staffName} ({role})
                    </div>
                )}
                {authToken && (
                    <div style={{ marginTop: 10 }}>
                        {role === 'admin' ? (
                            <>
                                <button onClick={() => {
                                    setShowReports(true);
                                    setShowSession(false);
                                    setShowStore(false);
                                    setShowPayments(false);
                                    localStorage.setItem('showReports', 'true');
                                    localStorage.setItem('showSession', 'false');
                                    localStorage.setItem('showStore', 'false');
                                    localStorage.setItem('showPayments', 'false');
                                }} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginRight: 10, marginBottom: 20 }}>View Reports</button>
                                <button onClick={() => {
                                    setShowStore(true);
                                    setShowSession(false);
                                    setShowReports(false);
                                    setShowPayments(false);
                                    localStorage.setItem('showStore', 'true');
                                    localStorage.setItem('showSession', 'false');
                                    localStorage.setItem('showReports', 'false');
                                    localStorage.setItem('showPayments', 'false');
                                }} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginRight: 10, marginBottom: 20 }}>Store Management</button>
                                <button onClick={() => {
                                    setShowPayments(true);
                                    setShowSession(false);
                                    setShowReports(false);
                                    setShowStore(false);
                                    localStorage.setItem('showPayments', 'true');
                                    localStorage.setItem('showSession', 'false');
                                    localStorage.setItem('showReports', 'false');
                                    localStorage.setItem('showStore', 'false');
                                }} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginRight: 10, marginBottom: 20 }}>Payments</button>
                            </>
                        ) : null}
                        <button
                            onClick={() => {
                                setShowSession(true);
                                setShowReports(false);
                                setShowStore(false);
                                setShowPayments(false);
                                localStorage.setItem('showSession', 'true');
                                localStorage.setItem('showReports', 'false');
                                localStorage.setItem('showStore', 'false');
                                localStorage.setItem('showPayments', 'false');
                            }}
                            style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginBottom: 20 }}
                        >
                            Sessions
                        </button>
                        <button onClick={handleSignout} style={{ padding: 10, borderRadius: 8, background: '#ff5252', color: '#fff', fontWeight: 600, fontSize: 16, marginLeft: 10, border: 'none', cursor: 'pointer', marginBottom: 20 }}>Sign Out</button>

                    </div>
                )}
            </header>
            {showPayments ? renderPayments() : showStore ? renderStore() : (showReports ? (role === 'admin' ? renderReports() : (
                <div style={{ maxWidth: 400, margin: '2rem auto', padding: 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002', textAlign: 'center' }}>
                    <h2>Access Denied</h2>
                    <p>Only admin users can view reports.</p>
                    <button onClick={() => {
                        setShowReports(false);
                        setShowSession(true);
                        localStorage.setItem('showReports', 'false');
                        localStorage.setItem('showSession', 'true');
                    }} style={{ marginTop: 20, padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Back</button>
                </div>
            )) : (
                <>
                    {!authToken && !showRegister && (
                        <div style={{ maxWidth: 400, margin: '2rem auto', padding: 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '13px' }}>
                            <h2 style={{ textAlign: 'center' }}>User Login</h2>
                            <input value={loginUsername} onChange={e => setLoginUsername(e.target.value)} placeholder="Username" style={{ width: '90%', marginBottom: 16, padding: 10, borderRadius: 8, border: 'none', fontSize: 18 }} />
                            <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Password" style={{ width: '90%', marginBottom: 16, padding: 10, borderRadius: 8, border: 'none', fontSize: 18 }} />
                            <button onClick={handleAuthLogin} style={{ width: '95%', padding: 12, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 18, border: 'none', cursor: 'pointer' }}>Login</button>
                            {/* <button onClick={() => setShowRegister(true)} style={{ width: '100%', padding: 12, borderRadius: 8, background: '#888', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginTop: 10 }}>Register</button> */}
                            {loginError && <div style={{ color: '#ff5252', marginTop: 10, textAlign: 'center' }}>{loginError}</div>}
                            {regSuccess && <div style={{ color: '#00e676', marginTop: 10, textAlign: 'center' }}>{regSuccess}</div>}
                        </div>
                    )}
                    {!authToken && showRegister && (
                        <div style={{ maxWidth: 400, margin: '2rem auto', padding: 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                            <h2 style={{ textAlign: 'center' }}>Register New User</h2>
                            <input value={regUsername} onChange={e => setRegUsername(e.target.value)} placeholder="Username" style={{ width: '100%', marginBottom: 16, padding: 10, borderRadius: 8, border: 'none', fontSize: 18 }} />
                            <input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Password" style={{ width: '100%', marginBottom: 16, padding: 10, borderRadius: 8, border: 'none', fontSize: 18 }} />
                            <select value={regRole} onChange={e => setRegRole(e.target.value)} style={{ width: '100%', marginBottom: 16, padding: 10, borderRadius: 8, border: 'none', fontSize: 18 }}>
                                <option value="admin">Admin</option>
                                <option value="staff">Staff</option>
                            </select>
                            <button onClick={handleRegister} style={{ width: '100%', padding: 12, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 18, border: 'none', cursor: 'pointer' }}>Register</button>
                            <button onClick={() => setShowRegister(false)} style={{ width: '100%', padding: 12, borderRadius: 8, background: '#888', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', marginTop: 10 }}>Back to Login</button>
                            {regError && <div style={{ color: '#ff5252', marginTop: 10, textAlign: 'center' }}>{regError}</div>}
                        </div>
                    )}
                    {authToken && (
                        <>
                            {/* Only show main app if logged in */}
                            {loggedIn && showSession && (() => {
                                const isMobile = window.innerWidth < 600;
                                return (
                                    <div style={{ maxWidth: 1200, margin: isMobile ? '1rem auto' : '2rem auto', padding: isMobile ? 20 : 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                                        <h2 style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 30, fontSize: isMobile ? '1.5rem' : '2rem' }}>Start New Session</h2>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 15 : 30, justifyContent: 'center' }}>
                                            {devices.map((dev, idx) => (
                                                <div key={idx} style={{ width: isMobile ? 140 : 160, height: isMobile ? 200 : 220, background: 'rgba(0,0,0,0.15)', borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px #0002', transition: 'transform 0.2s', border: activeSessions.some(s => s.device.name === dev.name) ? '2px solid  #1aeefd' : '2px solid #2a5298', opacity: activeSessions.some(s => s.device.name === dev.name) ? 1 : 0.5 }} onClick={() => !activeSessions.some(s => s.device.name === dev.name) && !startingDeviceNames.includes(dev.name) && handleStartSession(dev)}>
                                                    <img src={dev.img} alt={dev.name} style={{ width: 80, height: 80, marginBottom: 10 }} />
                                                    <span style={{ fontWeight: 600, fontSize: 18 }}>{dev.name}</span>
                                                    <span style={{ fontSize: 14, opacity: 0.7, color: '#b95ed6' }}>{dev.type}</span>
                                                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <label style={{ fontSize: 14, marginBottom: 8 }}>Rate/hr:</label>
                                                        <input
                                                            type="number"
                                                            step="1"
                                                            value={dev.rate}
                                                            onFocus={() => { deviceRateOriginalRef.current[idx] = dev.rate; }}
                                                            onChange={e => {
                                                                const newDevices = [...devices];
                                                                newDevices[idx].rate = Number(e.target.value);
                                                                setDevices(newDevices);
                                                                localStorage.setItem('devices', JSON.stringify(newDevices));
                                                            }}
                                                            onBlur={() => commitDeviceRate(idx)}
                                                            onClick={e => e.stopPropagation()}
                                                            style={{ padding: 5, borderRadius: 4, border: 'none', fontSize: 12, width: 60, textAlign: 'center', background: 'rgba(255,255,255,0.9)', color: '#000' }}
                                                        />
                                                    </div>
                                                    {/* {activeSessions.some(s => s.device.name === dev.name) && <span style={{ color: '#ff5252', fontWeight: 700, marginTop: 8 }}>Active</span>} */}
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 40, textAlign: 'center' }}>
                                            {/* <div style={{ marginBottom: 20 }}>
                                           <label style={{ fontWeight: 600 }}>Default Rate (per hour):
                                               <input type="number" value={rate} onChange={e => setRate(Number(e.target.value))} style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16, width: 80 }} />
                                           </label>
                                       </div> */}

                                            {/* Direct Snack Sale Section */}
                                            <div style={{ marginBottom: 30, padding: 20, background: 'rgba(0,0,0,0.1)', borderRadius: 12 }}>
                                                <h4 style={{ marginBottom: 15 }}>Quick Snack/Drink Sale</h4>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 10 }}>
                                                    <label style={{ fontWeight: 600 }}>Snack/Drink:
                                                        <select
                                                            value={snack}
                                                            onChange={e => {
                                                                setSnack(e.target.value);
                                                                const selectedItem = inventory.find(item => item.item === e.target.value);
                                                                if (selectedItem) setSnackPrice(selectedItem.price);
                                                            }}
                                                            style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16 }}
                                                        >
                                                            <option value="">Select Snack</option>
                                                            {inventory.map(item => <option key={item._id} value={item.item}>{item.item} ({availableStock(item.item)} left)</option>)}
                                                        </select>
                                                    </label>
                                                    <label style={{ fontWeight: 600 }}>Qty:
                                                        <input type="number" min={1} value={snackQty} onChange={e => setSnackQty(Number(e.target.value))} style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16, width: 60, textAlign: 'center' }} />
                                                    </label>
                                                    <label style={{ fontWeight: 600 }}>Price:
                                                        <input type="number" min={0} step="1" value={snackPrice} onChange={e => setSnackPrice(Number(e.target.value))} style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16, width: 80, textAlign: 'center' }} />
                                                    </label>
                                                    <button
                                                        onClick={async () => {
                                                            if (!snack) return;
                                                            const snackSale = {
                                                                category: 'Snack',
                                                                item: snack,
                                                                amount: snackQty,
                                                                price: snackPrice * snackQty
                                                            };
                                                            if (!(await askConfirm(`Record a quick sale of ${snackQty} × "${snack}" (${(snackPrice * snackQty).toLocaleString()})?`, { confirmLabel: 'Quick Sale' }))) return;
                                                            handleDirectSnackSale(snackSale);
                                                        }}
                                                        style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}
                                                    >
                                                        Quick Sale
                                                    </button>
                                                </div>
                                                {snackError && <div style={{ color: '#ff5252', marginTop: 10 }}>{snackError}</div>}
                                                {snackSuccess && <div style={{ color: '#00e676', marginTop: 10 }}>{snackSuccess}</div>}
                                            </div>

                                        </div>
                                    </div>
                                );
                            })()}
                            {loggedIn && showSession && activeSessions.length > 0 && (
                                <div style={{ maxWidth: 1200, margin: '2rem auto', padding: 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002' }}>
                                    <h2 style={{ textAlign: 'center', marginBottom: 30 }}>Active Sessions Monitoring</h2>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 30, justifyContent: 'center' }}>
                                        {activeSessions.map((session) => {
                                            const isEnding = endingSessionIds.includes(session._id);
                                            return (
                                                <div key={session._id} style={{ width: 320, background: 'rgba(0,0,0,0.15)', borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px #0002', border: '2px solid #1aeefd', padding: 20 }}>
                                                    <img src={session.device.img} alt={session.device.name} style={{ width: 80, height: 80, marginBottom: 10 }} />
                                                    <span style={{ fontWeight: 600, fontSize: 22 }}>{session.device.name}</span>
                                                    <span style={{ fontSize: 16, opacity: 0.7 }}>{session.device.type}</span>
                                                    <span style={{ fontSize: 16, margin: '10px 0', color: '#fff' }}>Started: {new Date(session.start).toLocaleTimeString()}</span>
                                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#00e676' }}>Elapsed: {Math.round((Date.now() - session.start) / 60000)} min</span>
                                                    <span style={{ fontSize: 16, margin: '10px 0', color: '#fff' }}>Rate: {session.rate} / hour</span>
                                                    {session.openedBy && <span style={{ fontSize: 14, margin: '4px 0', color: '#b95ed6' }}>Opened by: {session.openedBy}</span>}
                                                    <button onClick={() => handleEndSession(session._id)} disabled={isEnding} style={{ width: '100%', padding: 12, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 18, border: 'none', cursor: isEnding ? 'not-allowed' : 'pointer', margin: '10px 0', opacity: isEnding ? 0.6 : 1 }}>{isEnding ? 'Ending…' : 'End Session'}</button>
                                                    <div style={{ width: '100%', marginTop: 10 }}>
                                                        <h4 style={{ textAlign: 'center', marginBottom: 10 }}>Add Snack/Drink Sale</h4>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 10 }}>
                                                            <label style={{ fontWeight: 600 }}>Snack/Drink:
                                                                <select
                                                                    value={sessionSnacks[session._id]?.snack || ""}
                                                                    onChange={e => {
                                                                        const selectedItem = inventory.find(item => item.item === e.target.value);
                                                                        setSessionSnacks(prev => ({
                                                                            ...prev,
                                                                            [session._id]: {
                                                                                ...prev[session._id],
                                                                                snack: e.target.value,
                                                                                qty: prev[session._id]?.qty || 1,
                                                                                price: selectedItem ? selectedItem.price : (prev[session._id]?.price || 10)
                                                                            }
                                                                        }));
                                                                    }}
                                                                    style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16 }}
                                                                >
                                                                    <option value="">Select Snack</option>
                                                                    {inventory.map(item => <option key={item._id} value={item.item}>{item.item} ({availableStock(item.item)} left)</option>)}
                                                                </select>
                                                            </label>
                                                            <label style={{ fontWeight: 600 }}>Qty:
                                                                <input
                                                                    type="number"
                                                                    min={1}
                                                                    value={sessionSnacks[session._id]?.qty || 1}
                                                                    onChange={e => {
                                                                        const val = Number(e.target.value);
                                                                        setSessionSnacks(prev => ({
                                                                            ...prev,
                                                                            [session._id]: {
                                                                                snack: prev[session._id]?.snack || "",
                                                                                qty: isNaN(val) ? 1 : val,
                                                                                price: prev[session._id]?.price || 10
                                                                            }
                                                                        }));
                                                                    }}
                                                                    style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16, width: 60, textAlign: 'center' }}
                                                                />
                                                            </label>
                                                            <label style={{ fontWeight: 600 }}>Price:
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    step="1"
                                                                    value={sessionSnacks[session._id]?.price || 10}
                                                                    onChange={e => {
                                                                        const val = Number(e.target.value);
                                                                        setSessionSnacks(prev => ({
                                                                            ...prev,
                                                                            [session._id]: {
                                                                                snack: prev[session._id]?.snack || "",
                                                                                qty: prev[session._id]?.qty || 1,
                                                                                price: isNaN(val) ? 10 : val
                                                                            }
                                                                        }));
                                                                    }}
                                                                    style={{ marginLeft: 10, padding: 6, borderRadius: 6, border: 'none', fontSize: 16, width: 80, textAlign: 'center' }}
                                                                />
                                                            </label>
                                                            <button onClick={() => handleAddSnack(session._id)} style={{ padding: 10, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>Add Sale</button>
                                                        </div>
                                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                            {session.snacks.map((sale, sidx) => (
                                                                <li key={sidx} style={{ background: 'rgba(0,0,0,0.12)', margin: '8px 0', padding: 10, borderRadius: 8, fontSize: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                                                    <span>{sale.amount} x {sale.item} @ {Math.round(sale.price / sale.amount).toLocaleString()} = {Math.round(sale.price).toLocaleString()}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveSessionSnack(session._id, sidx)}
                                                                        title="Remove this snack"
                                                                        aria-label={`Remove ${sale.amount} x ${sale.item}`}
                                                                        style={{
                                                                            width: 28, height: 28, borderRadius: '50%',
                                                                            background: 'rgba(255,82,82,0.18)', color: '#ff5252',
                                                                            border: '1px solid rgba(255,82,82,0.4)', cursor: 'pointer',
                                                                            fontWeight: 700, fontSize: 16, lineHeight: 1,
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            flexShrink: 0, padding: 0
                                                                        }}
                                                                    >×</button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {saleSummaries.length > 0 && (
                                <div style={{ maxWidth: 600, margin: '2rem auto' }}>
                                    {saleSummaries.length > 1 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '0 8px' }}>
                                            <span style={{ fontSize: 14, opacity: 0.8 }}>{saleSummaries.length} pending invoices</span>
                                            <button
                                                onClick={() => setSaleSummaries([])}
                                                style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(255,82,82,0.18)', color: '#ff5252', border: '1px solid rgba(255,82,82,0.4)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                                            >
                                                Close all
                                            </button>
                                        </div>
                                    )}
                                    {saleSummaries.map(summary => (
                                        <div key={summary.id} style={{ padding: 30, background: 'rgba(255,255,255,0.07)', borderRadius: 16, boxShadow: '0 4px 24px #0002', textAlign: 'center', marginBottom: 16 }}>
                                            <h2>Sale Summary for {summary.device}</h2>
                                            {(summary.openedBy || summary.closedBy) && (
                                                <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10, color: '#b95ed6' }}>
                                                    {summary.openedBy && <>Opened by: <strong>{summary.openedBy}</strong></>}
                                                    {summary.openedBy && summary.closedBy && <> &nbsp;·&nbsp; </>}
                                                    {summary.closedBy && <>Closed by: <strong>{summary.closedBy}</strong></>}
                                                </div>
                                            )}
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                {summary.items.map((item, idx) => (
                                                    <li key={idx} style={{ background: 'rgba(0,0,0,0.12)', margin: '8px 0', padding: 10, borderRadius: 8, fontSize: 16 }}>
                                                        {item.category}: {item.item} - {item.amount} x {Math.round(item.price / item.amount).toLocaleString()} = {Math.round(item.price).toLocaleString()}
                                                    </li>
                                                ))}
                                            </ul>
                                            <h3 style={{ fontSize: 24, margin: '20px 0' }}>Total: {Math.round(summary.total).toLocaleString()}</h3>
                                            <p>Stock reduced automatically for snacks/drinks.</p>
                                            <button
                                                onClick={() => setSaleSummaries(prev => prev.filter(s => s.id !== summary.id))}
                                                style={{ padding: 14, borderRadius: 8, background: '#2a5298', color: '#fff', fontWeight: 600, fontSize: 18, border: 'none', cursor: 'pointer', marginTop: 20 }}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            ))}
            <footer style={{ textAlign: 'center', padding: '2rem 0', opacity: 0.7, fontSize: 16 }}>
                &copy; {new Date().getFullYear()} SpaceX Gaming Cafe. All rights reserved.
            </footer>

            {/* Global confirmation dialog — shown by askConfirm() before any add/remove/edit action */}
            {confirmDialog && (
                <div
                    onClick={() => resolveConfirm(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        style={{ width: '100%', maxWidth: 420, background: 'linear-gradient(135deg, #2a2145 0%, #352a52 100%)', borderRadius: 16, boxShadow: '0 8px 40px #000a', border: '1px solid rgba(255,255,255,0.12)', padding: 26, color: '#fff' }}
                    >
                        <h3 style={{ margin: '0 0 12px 0', fontSize: 20, fontWeight: 700 }}>Please Confirm</h3>
                        <p style={{ margin: '0 0 24px 0', fontSize: 16, lineHeight: 1.5, opacity: 0.9 }}>{confirmDialog.message}</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button
                                onClick={() => resolveConfirm(false)}
                                style={{ padding: '10px 18px', borderRadius: 8, background: '#888', color: '#fff', fontWeight: 600, fontSize: 15, border: 'none', cursor: 'pointer' }}
                            >Cancel</button>
                            <button
                                autoFocus
                                onClick={() => resolveConfirm(true)}
                                style={{ padding: '10px 18px', borderRadius: 8, background: confirmDialog.danger ? '#ff5252' : '#2a5298', color: '#fff', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}
                            >{confirmDialog.confirmLabel || 'Confirm'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}



export default App;
